import { supabase } from './supabase';

const USER_STORAGE_KEY = 'user';
const AUTH_CHANGED_EVENT = 'tourasco-auth-changed';

function emitAuthChanged() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
  }
}

export function subscribeAuthChanged(callback) {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(AUTH_CHANGED_EVENT, callback);
  window.addEventListener('storage', callback);
  return () => {
    window.removeEventListener(AUTH_CHANGED_EVENT, callback);
    window.removeEventListener('storage', callback);
  };
}

export function getStoredUser() {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function setStoredUser(user) {
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  emitAuthChanged();
}

export function clearStoredUser() {
  localStorage.removeItem(USER_STORAGE_KEY);
  emitAuthChanged();
}

/**
 * Register a new app user (RPC; password hashed in Postgres with pgcrypto).
 */
export async function registerAppUser({ email, password, firstName, lastName, phone }) {
  const { data, error } = await supabase.rpc('register_app_user', {
    p_email: email,
    p_password: password,
    p_first_name: firstName,
    p_last_name: lastName,
    p_phone: phone || null,
  });

  if (error) {
    throw new Error(error.message || 'Registration failed');
  }

  if (!data || !data.success) {
    throw new Error(data?.error || 'Registration failed');
  }

  return data;
}

/**
 * Login with email + password (RPC; verification in Postgres).
 */
export async function loginAppUser(email, password) {
  const { data, error } = await supabase.rpc('login_app_user', {
    p_email: email,
    p_password: password,
  });

  if (error) {
    throw new Error(error.message || 'Sign in failed');
  }

  if (!data || !data.success) {
    throw new Error(data?.error || 'Invalid email or password');
  }

  const u = data.user;
  return {
    id: u.id,
    email: u.email,
    firstName: u.firstName,
    lastName: u.lastName,
    phone: u.phone ?? '',
  };
}

/**
 * Permanently delete the account in the database (requires correct password).
 */
export async function deleteAppUser(email, password) {
  const { data, error } = await supabase.rpc('delete_app_user', {
    p_email: email,
    p_password: password,
  });

  if (error) {
    throw new Error(error.message || 'Could not delete account');
  }

  if (!data || !data.success) {
    throw new Error(data?.error || 'Could not delete account');
  }

  return data;
}
